import { Check, CircleX, PartyPopper, TriangleAlert } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useSplashStore } from "../../hooks/use-store";

export function SplashScreen() {
	const { isOpen, title, description, variant, hide } = useSplashStore();

	const getIcon = () => {
		switch (variant) {
			case "celebration":
				return <PartyPopper className="w-10 h-10" />;
			case "error":
				return <CircleX className="w-10 h-10" />;
			case "alert":
				return <TriangleAlert className="w-10 h-10" />;
			default:
				return <Check className="w-10 h-10" />;
		}
	};

	const getIconBgColor = () => {
		switch (variant) {
			case "error":
				return "bg-destructive text-destructive-foreground";
			case "alert":
				return "bg-yellow-500 text-white";
			default:
				return "bg-primary text-primary-foreground";
		}
	};

	return (
		<AnimatePresence>
			{isOpen && (
				<button
					className="fixed inset-0 z-50 flex items-center justify-center pointer-events-auto cursor-pointer"
					onClick={hide}
					type="button"
				>
					{/* Backdrop */}
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						className="absolute inset-0 bg-black/40 backdrop-blur-sm"
					/>

					<motion.div
						initial={{ y: 50, opacity: 0 }}
						animate={{ y: 0, opacity: 1 }}
						exit={{ y: 50, opacity: 0 }}
						transition={{ type: "spring", stiffness: 400, damping: 25 }}
						className="relative z-10 p-8 rounded-2xl bg-card border border-border/50 shadow-2xl flex flex-col items-center gap-6 max-w-sm w-full mx-4 text-center overflow-hidden cursor-default"
					>
						{/* Background Decoration */}
						<div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/10" />

						{/* Icon */}
						<motion.div
							initial={{ y: -20, rotate: -45 }}
							animate={{ y: 0, rotate: 0 }}
							transition={{ delay: 0.1 }}
							className={`relative z-10 flex items-center justify-center w-20 h-20 rounded-full shadow-lg ${getIconBgColor()}`}
						>
							{getIcon()}
						</motion.div>

						{/* Text Content */}
						<div className="relative z-10 space-y-2">
							<motion.h2
								initial={{ opacity: 0, y: 10 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ delay: 0.2 }}
								className="text-2xl font-bold tracking-tight"
							>
								{title}
							</motion.h2>
							{description && (
								<motion.p
									initial={{ opacity: 0, y: 10 }}
									animate={{ opacity: 1, y: 0 }}
									transition={{ delay: 0.3 }}
									className="text-muted-foreground font-medium"
								>
									{description}
								</motion.p>
							)}
						</div>
					</motion.div>

					{/* Confetti Effect for Celebration Variant */}
					{variant === "celebration" && <Confetti />}
				</button>
			)}
		</AnimatePresence>
	);
}

// Simple CSS/Framer Motion Confetti
function Confetti() {
	const colors = [
		"#ef4444",
		"#f97316",
		"#eab308",
		"#22c55e",
		"#3b82f6",
		"#a855f7",
	];

	return (
		<div className="absolute inset-0 overflow-hidden pointer-events-none">
			{Array.from({ length: 30 }).map((_, i) => (
				<motion.div
					key={i}
					initial={{
						x: "50vw",
						y: "50vh",
						scale: 0,
					}}
					animate={{
						x: `${Math.random() * 100}vw`,
						y: `${Math.random() * 100}vh`,
						scale: [0, 1, 0.5, 0],
						rotate: Math.random() * 360,
					}}
					transition={{
						duration: 1.5 + Math.random(),
						ease: "easeOut",
						delay: Math.random() * 0.2,
					}}
					style={{
						position: "absolute",
						width: "10px",
						height: "10px",
						backgroundColor: colors[Math.floor(Math.random() * colors.length)],
						borderRadius: Math.random() > 0.5 ? "50%" : "2px",
					}}
				/>
			))}
		</div>
	);
}
